import { ArrowDown, Heart, Shield, Star, Users } from "lucide-react";
import { useState } from "react";
import {
  FaEnvelope,
  FaFacebook,
  FaInstagram,
  FaWhatsapp,
} from "react-icons/fa";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { config } from "@/config/config";
import { TPlanResponse } from "@/pages/clients/use-client-plans.hooks";
import { formatPrice } from "@/utils/monetary-formatter";

interface InsuranceCombo {
  id: number;
  name: string;
  price: number;
  popular?: boolean;
  features: string[];
  description: string;
  savings?: string;
}

const combos: InsuranceCombo[] = [
  {
    id: 1,
    name: "Combo Essencial",
    price: 69.9,
    description: "Proteção básica para você e sua família",
    features: [
      "Seguro Acidentes Pessoais",
      "Funeral Individual",
      "Seguro de Vida",
    ],
  },
  {
    id: 2,
    name: "Combo Completo",
    price: 109.9,
    popular: true,
    description: "Nossa opção mais popular com cobertura ampliada",
    savings: "Mais escolhido",
    features: [
      "Seguro Acidentes Pessoais",
      "Funeral Individual",
      "Seguro de Vida",
      "Cobertura ampliada",
      "Suporte prioritário",
    ],
  },
  {
    id: 3,
    name: "Combo Premium",
    price: 149.9,
    description: "Máxima proteção com benefícios exclusivos",
    savings: "Melhor valor",
    features: [
      "Seguro Acidentes Pessoais",
      "Funeral Individual",
      "Seguro de Vida",
      "Cobertura máxima",
      "Suporte VIP 24/7",
      "Benefícios exclusivos",
    ],
  },
];

interface IInsuranceLandingPageProps {
  title: string;
  description: string;
  primaryButtonText: string;
  imageBannerPrimary?: string;
  primaryLogo?: string;
  secondaryLogo?: string;
  whatsappNumber?: string;
  instagramUrl?: string;
  facebookUrl?: string;
  contactEmail?: string;
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
  titleFontFamily?: string;
  bodyFontFamily?: string;
  selectedPlans?: TPlanResponse[];
}

export default function InsuranceLandingPage({
  title,
  description,
  primaryButtonText,
  imageBannerPrimary,
  primaryLogo,
  secondaryLogo,
  whatsappNumber,
  instagramUrl,
  facebookUrl,
  contactEmail,
  primaryColor,
  secondaryColor,
  backgroundColor,
  titleFontFamily,
  bodyFontFamily,
  selectedPlans,
}: IInsuranceLandingPageProps) {
  const [selectedCombo] = useState<string | null>(null);
  useState<InsuranceCombo | null>(null);

  // Helper function to get darker shade of primary color
  const getDarkerPrimaryColor = () => {
    if (!primaryColor) return "#5a4280";
    // Simple darken by reducing brightness
    const hex = primaryColor.replace("#", "");
    const r = Math.max(0, parseInt(hex.substr(0, 2), 16) - 30);
    const g = Math.max(0, parseInt(hex.substr(2, 2), 16) - 30);
    const b = Math.max(0, parseInt(hex.substr(4, 2), 16) - 30);
    return `#${r.toString(16).padStart(2, "0")}${g
      .toString(16)
      .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
  };

  return (
    <div 
      className="min-h-screen overflow-x-hidden"
      style={{
        backgroundColor: backgroundColor || "#ffffff",
        fontFamily: bodyFontFamily || "Inter, sans-serif",
      }}
    >
      {/* Header */}
      <header 
        className="sticky top-0 z-50 w-full border-b backdrop-blur supports-[backdrop-filter]:bg-background/60"
        style={{
          backgroundColor: backgroundColor ? `${backgroundColor}95` : "rgba(255, 255, 255, 0.95)",
        }}
      >
        <div className="container flex h-28 items-center justify-between px-10">
          <div className="flex items-center space-x-8">
            {primaryLogo && (
              <img
                src={primaryLogo}
                alt={config.brandName}
                className="max-h-24 max-w-[16rem] object-contain"
                style={{ aspectRatio: "3/1" }}
              />
            )}
            {secondaryLogo && (
              <img
                src={secondaryLogo}
                alt={config.brandName}
                className="max-h-24 max-w-[16rem] object-contain"
                style={{ aspectRatio: "3/1" }}
              />
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section
        className="relative py-20 md:py-32 overflow-hidden bg-gradient-to-br"
        style={{
          background: primaryColor
            ? `linear-gradient(135deg, ${primaryColor}0D 5%, ${primaryColor}1A 100%)`
            : "linear-gradient(135deg, #6d4f971A 5%, #6d4f9726 100%)",
        }}
      >
        <div className="container relative mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="space-y-6">
              <Badge
                variant="secondary"
                className="w-fit"
                style={{
                  backgroundColor: primaryColor
                    ? `${primaryColor}1A`
                    : "#6d4f971A",
                  color: primaryColor || "#6d4f97",
                }}
              >
                Proteção Completa
              </Badge>
              <h1 
                className="text-4xl md:text-6xl font-bold tracking-tight text-gray-900"
                style={{ fontFamily: titleFontFamily || "Inter, sans-serif" }}
              >
                <span
                  className="block"
                  style={{ color: primaryColor || "#6d4f97" }}
                >
                  {title}
                </span>
              </h1>
              <span dangerouslySetInnerHTML={{ __html: description }} />
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="text-lg px-8"
                  style={{
                    backgroundColor: primaryColor || "#6d4f97",
                    borderColor: primaryColor || "#6d4f97",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      getDarkerPrimaryColor();
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      primaryColor || "#6d4f97";
                  }}
                >
                  {primaryButtonText}
                </Button>
              </div>
              <div className="flex items-center space-x-6 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <Shield className="h-4 w-4 text-green-600" />
                  <span>100% Seguro</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  <span>4.9 de satisfação</span>
                </div>
              </div>
            </div>
            <div className="relative overflow-hidden w-full">
              <div className="flex justify-center items-center w-full">
                {imageBannerPrimary && (
                  <img
                    src={imageBannerPrimary}
                    alt="Preview Imagem Primária"
                    className="max-h-96 w-full max-w-lg rounded border object-contain"
                    style={{ aspectRatio: "4/3" }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Combos Section */}
      <section id="combos" className="py-20">
        <div>
          <div className="text-center space-y-4 mb-12">
            <h2 
              className="text-3xl md:text-4xl font-bold text-gray-900"
              style={{ fontFamily: titleFontFamily || "Inter, sans-serif" }}
            >
              Nossos Planos
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Escolha o plano ideal para sua proteção. Todos incluem as três
              coberturas essenciais.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {selectedPlans?.map((plan) => (
              <Card
                key={plan.id}
                className={`relative overflow-hidden hover:shadow-xl transition-all duration-300 ${
                  plan.is_active ? "ring-2 scale-105" : ""
                } ${selectedCombo === plan.id ? "ring-2 ring-green-600" : ""}`}
                style={{
                  borderColor: plan.is_active
                    ? primaryColor || "#6d4f97"
                    : undefined,
                }}
              >
                {plan.is_active && (
                  <Badge
                    className="absolute top-4 left-1/2 transform -translate-x-1/2"
                    style={{
                      backgroundColor: primaryColor || "#6d4f97",
                      borderColor: primaryColor || "#6d4f97",
                    }}
                  >
                    Mais Popular
                  </Badge>
                )}
                {/* {plan.savings && !plan.popular && (
                  <Badge className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-green-600">
                    {plan.savings}
                  </Badge>
                )} */}

                <CardHeader className="text-center pt-8">
                  <CardTitle 
                    className="text-2xl font-bold text-gray-900"
                    style={{ fontFamily: titleFontFamily || "Inter, sans-serif" }}
                  >
                    {plan.name}
                  </CardTitle>
                  <CardDescription className="text-gray-600">
                    {plan.description}
                  </CardDescription>
                  <div className="py-4">
                    <span
                      className="text-4xl font-bold"
                      style={{ color: primaryColor || "#6d4f97" }}
                    >
                      {formatPrice(plan.plan_price)}
                    </span>
                    <span className="text-gray-600 ml-2">/mês</span>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    {plan.products?.map((feature, index) => (
                      <>
                        {feature.insurance_benefits && (
                          <div
                            key={index}
                            className="flex items-center space-x-3 ml-4"
                          >
                            <span
                              dangerouslySetInnerHTML={{
                                __html: feature.insurance_benefits,
                              }}
                            />
                          </div>
                        )}
                      </>
                    ))}
                  </div>
                </CardContent>

                <CardFooter className="pt-0">
                  <Button
                    className="w-full"
                    style={{
                      backgroundColor: plan.is_active
                        ? primaryColor || "#6d4f97"
                        : selectedCombo === plan.id
                        ? "#16a34a"
                        : secondaryColor || "#111827",
                      borderColor: plan.is_active
                        ? primaryColor || "#6d4f97"
                        : selectedCombo === plan.id
                        ? "#16a34a"
                        : secondaryColor || "#111827",
                    }}
                    onMouseEnter={(e) => {
                      if (plan.is_active) {
                        e.currentTarget.style.backgroundColor =
                          getDarkerPrimaryColor();
                      } else if (selectedCombo === plan.id) {
                        e.currentTarget.style.backgroundColor = "#15803d";
                      } else {
                        e.currentTarget.style.backgroundColor =
                          secondaryColor || "#1f2937";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (plan.is_active) {
                        e.currentTarget.style.backgroundColor =
                          primaryColor || "#6d4f97";
                      } else if (selectedCombo === plan.id) {
                        e.currentTarget.style.backgroundColor = "#16a34a";
                      } else {
                        e.currentTarget.style.backgroundColor =
                          secondaryColor || "#1f2937";
                      }
                    }}
                  >
                    {selectedCombo === plan.id
                      ? "Selecionado"
                      : "Escolher Plano"}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
        <div className="flex justify-center mt-10">
          <Button
            size="lg"
            className="flex items-center gap-2 px-8 bg-white border transition-colors shadow"
            style={{
              color: primaryColor || "#6d4f97",
              borderColor: primaryColor || "#6d4f97",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = primaryColor || "#6d4f97";
              e.currentTarget.style.color = "white";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "white";
              e.currentTarget.style.color = primaryColor || "#6d4f97";
            }}
            onClick={() => {
              const section = document.querySelector("#combos-compare");
              if (section) {
                section.scrollIntoView({ behavior: "smooth" });
              }
            }}
          >
            Compare os planos
            <ArrowDown className="h-5 w-5" />
          </Button>
        </div>
      </section>

      <section id="combos-compare" className="py-20">
        <div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {selectedPlans?.map((plan) => (
              <Card
                key={plan.id}
                className={
                  "relative overflow-hidden hover:shadow-xl transition-all duration-300"
                }
              >
                <CardHeader className="text-center pt-8">
                  <CardTitle 
                    className="text-2xl font-bold text-gray-900"
                    style={{ fontFamily: titleFontFamily || "Inter, sans-serif" }}
                  >
                    {plan.name}
                  </CardTitle>
                  <CardDescription className="text-gray-600">
                    {plan.description}
                  </CardDescription>
                  <div className="py-4">
                    <span
                      className="text-4xl font-bold"
                      style={{ color: primaryColor || "#6d4f97" }}
                    >
                      {formatPrice(plan.plan_price)}
                    </span>
                    <span className="text-gray-600 ml-2">/mês</span>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    {plan.products.map((feature, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-3 ml-4"
                      >
                        <span className="text-gray-700">
                          <span
                            dangerouslySetInnerHTML={{
                              __html: feature.insurance_coverages,
                            }}
                          />
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section 
        className="py-20"
        style={{
          backgroundColor: backgroundColor ? `${backgroundColor}10` : "#f9fafb",
        }}
      >
        <div className="ml-10 mr-10">
          <div className="text-center space-y-4 mb-12">
            <h2 
              className="text-3xl md:text-4xl font-bold text-gray-900"
              style={{ fontFamily: titleFontFamily || "Inter, sans-serif" }}
            >
              Por que escolher a Clube Infinity?
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div
                className="h-16 w-16 rounded-full flex items-center justify-center mx-auto"
                style={{
                  backgroundColor: primaryColor
                    ? `${primaryColor}1A`
                    : "rgba(109, 79, 151, 0.1)",
                }}
              >
                <Shield
                  className="h-8 w-8"
                  style={{ color: primaryColor || "#6d4f97" }}
                />
              </div>
              <h3 
                className="text-xl font-semibold text-gray-900"
                style={{ fontFamily: titleFontFamily || "Inter, sans-serif" }}
              >
                Proteção Completa
              </h3>
              <p className="text-gray-600">
                Cobertura abrangente para todas as situações do dia a dia
              </p>
            </div>
            <div className="text-center space-y-4">
              <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <Users className="h-8 w-8 text-green-600" />
              </div>
              <h3 
                className="text-xl font-semibold text-gray-900"
                style={{ fontFamily: titleFontFamily || "Inter, sans-serif" }}
              >
                Atendimento Humanizado
              </h3>
              <p className="text-gray-600">
                Suporte especializado quando você mais precisa
              </p>
            </div>
            <div className="text-center space-y-4">
              <div className="h-16 w-16 rounded-full bg-purple-100 flex items-center justify-center mx-auto">
                <Heart className="h-8 w-8 text-purple-600" />
              </div>
              <h3 
                className="text-xl font-semibold text-gray-900"
                style={{ fontFamily: titleFontFamily || "Inter, sans-serif" }}
              >
                Tranquilidade
              </h3>
              <p className="text-gray-600">
                Durma tranquilo sabendo que você está protegido
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-20"
        style={{ backgroundColor: primaryColor || "#6d4f97" }}
      >
        <div>
          <div className="text-center text-white">
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ fontFamily: titleFontFamily || "Inter, sans-serif" }}
            >
              Pronto para se proteger?
            </h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Não deixe para amanhã a proteção que sua família precisa hoje.
              Contrate agora e tenha paz de espírito.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" className="text-lg px-8">
                <FaWhatsapp color={getDarkerPrimaryColor()} className="mr-2" />
                Contratar Agora
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer 
        className="border-t py-12 text-white"
        style={{
          backgroundColor: backgroundColor ? `${backgroundColor}90` : "#111827",
        }}
      >
        <div className="ml-10">
          <div className="border-b border-gray-800 mb-8 pb-8 text-center text-sm text-gray-400">
            <div className="flex justify-center gap-6">
              <div className="flex items-center justify-center">Contatos:</div>
              {whatsappNumber && (
                <a
                  href={`https://wa.me/${whatsappNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Whatsapp"
                  className="hover:text-green-400 transition-colors"
                >
                  <FaWhatsapp size={25} color="#fff" />
                </a>
              )}
              {instagramUrl && (
                <a
                  href={instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="hover:text-pink-400 transition-colors"
                >
                  <FaInstagram size={25} color="#fff" />
                </a>
              )}
              {facebookUrl && (
                <a
                  href={facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="hover:text-blue-400 transition-colors"
                >
                  <FaFacebook size={25} color="#fff" />
                </a>
              )}
              {contactEmail && (
                <a
                  href={`mailto:${contactEmail}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Email"
                  className="hover:text-blue-400 transition-colors"
                >
                  <FaEnvelope size={25} color="#fff" />
                </a>
              )}
            </div>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center justify-center md:justify-start">
                <img src={config.logoUrl} alt={config.brandName} className="w-32 h-16" />
              </div>
              <p className="text-gray-400">
                Proteção e segurança para toda sua família.
              </p>
            </div>
            <div className="space-y-4">
              <h4 
                className="font-semibold"
                style={{ fontFamily: titleFontFamily || "Inter, sans-serif" }}
              >Produtos</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <Button variant="link" className="block hover:text-white">
                  Seguro de Vida
                </Button>
                <Button variant="link" className="block hover:text-white">
                  Acidentes Pessoais
                </Button>
                <Button variant="link" className="block hover:text-white">
                  Funeral Individual
                </Button>
              </div>
            </div>
            <div className="space-y-4">
              <h4 
                className="font-semibold"
                style={{ fontFamily: titleFontFamily || "Inter, sans-serif" }}
              >Empresa</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <Button variant="link" className="block hover:text-white">
                  Sobre Nós
                </Button>
                <Button variant="link" className="block hover:text-white">
                  Carreiras
                </Button>
                <Button variant="link" className="block hover:text-white">
                  Imprensa
                </Button>
              </div>
            </div>
            <div className="space-y-4">
              <h4 
                className="font-semibold"
                style={{ fontFamily: titleFontFamily || "Inter, sans-serif" }}
              >Suporte</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <Button variant="link" className="block hover:text-white">
                  Central de Ajuda
                </Button>
                <Button variant="link" className="block hover:text-white">
                  Termos de Uso
                </Button>
                <Button variant="link" className="block hover:text-white">
                  Política de Privacidade
                </Button>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>
              &copy; {new Date().getFullYear()} Clube Infinity. Todos os
              direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
